import React, { useRef, useState, useEffect } from "react";
import { FormControl, Card, Layout, Button, usePath, CardBody, useAppConfig } from "@fdoh/health-ui-react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useSubmitForm } from "../../hooks/useSubmitForm";
import { useForm } from "react-hook-form";
import { TankInstallationInput, schema, TankInstallationFormProps, ComplianceData, TankMaterialData, LegendData, OutletFilterData, AtuPbtData } from "./TankInstallationForm.types";
import { useData } from "../../hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import CSS from 'csstype';
import { Constants, InspectionStatus } from "../../helpers/Constants";
import { toast } from "react-toastify";
import { InspectionStatusInput, FinalInspectionPrePopulate } from "../InspectionHome/InspectionHome.types";
import useProfile from "../../hooks/useProfile";
import { UserSecurityInput } from "../../components/DBProtectedRoute.types";

export const TankInstallationForm = (props: TankInstallationFormProps) => {
    const history = useHistory();
    const path = usePath();
    const { state }: any = useLocation();
    const applicationNumber = state.applicationNumber;
    const lastInspectionStatusData = state.lastInspectionStatusData;
    const complianceData = useData<ComplianceData[]>("Compliance") || [];
    const tankMaterialData = useData<TankMaterialData[]>("TankMaterial") || [];
    const legendData = useData<LegendData[]>("Legend") || [];
    const outletFilterData = useData<OutletFilterData[]>("OutletFilter") || [];
    const atuPbtData = useData<AtuPbtData[]>("AtuPbt") || [];
    const [isTank2Showable, setIsTank2Showable] = useState(props.values?.tank2ComplianceId ? true : false);
    const inspectionStatusData = useData<InspectionStatusInput>("inspectionDetails/" + props.applicationInspectionId);
    const defaultvalues = {
        ...props.values,
        dateCreated: props.values?.dateCreated?.toLocaleString().slice(0, 10),
        isTank2Showable: props.values?.tank2ComplianceId ? true : false,
        atuPbtComplianceId: props.values?.atuPbtComplianceId || 0,
        atuPbtId: props.values?.atuPbtId || 0,
        atuPbtComment: props.values?.atuPbtComment || '',
        tank2AtuPbtComplianceId: props.values?.tank2AtuPbtComplianceId || 0,
        tank2AtuPbtId: props.values?.tank2AtuPbtId || 0,
        tank2AtuPbtComment: props.values?.tank2AtuPbtComment || ''
    }
    // Normalize multichambered defaults so Selects (1/0) show correctly on load
    const normalizedDefaults = {
        ...defaultvalues,
        multiChambered: (defaultvalues as any)?.multiChambered === true ? 1
            : (defaultvalues as any)?.multiChambered === false ? 0
            : (defaultvalues as any)?.multiChambered,
        inSeries: (defaultvalues as any)?.inSeries === true ? 1
            : (defaultvalues as any)?.inSeries === false ? 0
            : (defaultvalues as any)?.inSeries,
        risers: (defaultvalues as any)?.risers === true ? 1
            : (defaultvalues as any)?.risers === false ? 0
            : (defaultvalues as any)?.risers,
        tank2MultiChambered: (defaultvalues as any)?.tank2MultiChambered === true ? 1
            : (defaultvalues as any)?.tank2MultiChambered === false ? 0
            : (defaultvalues as any)?.tank2MultiChambered,
        tank2InSeries: (defaultvalues as any)?.tank2InSeries === true ? 1
            : (defaultvalues as any)?.tank2InSeries === false ? 0
            : (defaultvalues as any)?.tank2InSeries,
        tank2Risers: (defaultvalues as any)?.tank2Risers === true ? 1
            : (defaultvalues as any)?.tank2Risers === false ? 0
            : (defaultvalues as any)?.tank2Risers,
        // Tank Material IDs (if Other Description has value, force 'Other' selection)
        tankMaterialId1:
            (((defaultvalues as any)?.tankMaterialOtherDescription ?? '')
                .toString()
                .trim().length > 0)
                ? -1
                : ((defaultvalues as any)?.tankMaterialId1 ?? ''),
        tankMaterialId2:
            (((defaultvalues as any)?.tank2MaterialOtherDescription ?? '')
                .toString()
                .trim().length > 0)
                ? -1
                : ((defaultvalues as any)?.tankMaterialId2 ?? ''),
                        // New: Tank Material Other Description fields
                tankMaterialOtherDescription: (defaultvalues as any)?.tankMaterialOtherDescription || '',
                tank2MaterialOtherDescription: (defaultvalues as any)?.tank2MaterialOtherDescription || '',
                // New: Tank Size Units fields
                tankSize1Units: (defaultvalues as any)?.tankSize1Units || '',
                tankSize2Units: (defaultvalues as any)?.tankSize2Units || '',
    } as any;
    const { control, handleSubmit, setValue, watch } = useForm<TankInstallationInput>({
        resolver: yupResolver(schema),
        defaultValues: normalizedDefaults,
    });
    const formRef = useRef<HTMLFormElement>(null);
    const config = useAppConfig();
    const application = config.applications.filter(x => x.name === "privateProviderInspectionApi")[0];

    const user = useProfile(application) as UserSecurityInput;
    
    // Watch for changes in tank material selections to clear "Other" descriptions
    const tankMaterialId1 = watch("tankMaterialId1");
    const tankMaterialId2 = watch("tankMaterialId2");
    
    // Clear "Other" descriptions when different material types are selected
    useEffect(() => {
        if ((tankMaterialId1 as any) !== -1 && (tankMaterialId1 as any) !== '' && (tankMaterialId1 as any) !== 0) {
            setValue("tankMaterialOtherDescription", "");
        }
    }, [tankMaterialId1, setValue]);
    
    useEffect(() => {
        if ((tankMaterialId2 as any) !== -1 && (tankMaterialId2 as any) !== '' && (tankMaterialId2 as any) !== 0) {
            setValue("tank2MaterialOtherDescription", "");
        }
    }, [tankMaterialId2, setValue]);
    
    function timeout(delay: number) {
        return new Promise(res => setTimeout(res, delay));
    }

    const onSubmit = useSubmitForm<TankInstallationInput>(
        'tankInstallation',
        async (response) => {
            toast.success("Tank Installation Saved Successfully");
            await timeout(2000);
            let inspectionStatus = watch("inspectionStatusId");
            if (inspectionStatus == InspectionStatus.Complete) {
                if (inspectionStatusData?.drainfieldInstallationInspectionId == null
                    || inspectionStatusData?.drainfieldInstallationInspectionId == undefined) {
                    history.push({
                        pathname: "/drainfieldInstallation/",
                        search: `?applicationId=${inspectionStatusData?.applicationId}&inspectionId=${inspectionStatusData?.applicationInspectionId}`,
                        state: { lastInspectionStatusData: lastInspectionStatusData, applicationNumber: applicationNumber }
                    })
                }
                else {
                    history.push({
                        pathname: "/drainfieldInstallation/" + inspectionStatusData?.drainfieldInstallationId,
                        search: `?applicationId=${inspectionStatusData?.applicationId}&inspectionId=${inspectionStatusData?.applicationInspectionId}`,
                        state: { lastInspectionStatusData: lastInspectionStatusData, applicationNumber: applicationNumber }
                    })
                }
            }
            else if (inspectionStatus == InspectionStatus.Incomplete) {
                history.push({
                    pathname: `/inspectionDetails/${inspectionStatusData?.applicationInspectionId}`,
                    state: { lastInspectionStatusData: lastInspectionStatusData, applicationNumber: applicationNumber }
                });
            }
        },
        props.action
    );


    const removeClick = (e: any) => {

        e.preventDefault();
    };


    const submit = () => {
        setValue('applicationInspectionId', props.applicationInspectionId ? props.applicationInspectionId : 0);

        if (inspectionStatusData?.tankInstallationInspectionId == null
            || inspectionStatusData?.tankInstallationInspectionId == undefined) {
            setValue('dateCreated', new Date());
            setValue('createdBy', user?.userName);
        }
        else {
            setValue('dateUpdated', new Date());
            setValue('updatedBy', user?.userName);
        }

        setValue('isTank2Showable', isTank2Showable);

        if (!isTank2Showable) {
            setValue('tank2ComplianceId', 0);
            setValue('tankSize2', 1);
            setValue('tank2SizeComment', 'Remove Tank-2');

            setValue('tank2MaterialComplianceId', 0);
            setValue('tankMaterialId2', 0);
            setValue('tank2MaterialComment', 'Remove Tank-2');
            // Clear new Tank 2 Material Other Description
            setValue('tank2MaterialOtherDescription', '');

            setValue('tank2LegendComplianceId', 0);
            setValue('legendId2', 0);
            setValue('tank2LegendComment', 'Remove Tank-2');

            setValue('tank2OutletDeviceComplianceId', 0);
            setValue('tank2OutletDeviceComment', 'Remove Tank-2');

            setValue('tank2MultiChamberedComplianceId', 0);
            setValue('tank2MultiChambered', false);
            setValue('tank2MultiChamberedComment', 'Remove Tank-2');

            // Clear new Tank 2 In Series value
            setValue('tank2InSeries', false);
            // Clear new Tank 2 Riser(s) value
            setValue('tank2Risers', false);

            setValue('tank2OutletFilterComplianceId', 0);
            setValue('tank2OutletFilterId', 0);
            setValue('tank2OutletFilterComment', 'Remove Tank-2');

            setValue('tank2WatertightComplianceId', 0);
            setValue('tank2WatertightComment', 'Remove Tank-2');

            setValue('tank2LevelComplianceId', 0);
            setValue('tank2LevelComment', 'Remove Tank-2');

            setValue('tank2DepthToLidComplianceId', 0);
            setValue('tank2DepthToLidComment', 'Remove Tank-2');

            setValue('tank2AtuPbtComplianceId', 0);
            setValue('tank2AtuPbtId', 0);
            setValue('tank2AtuPbtComment', 'Remove Tank-2');
        }

        // Handle "Other" material type selections before submission
        if (watch("tankMaterialId1") === -1 && !watch("tankMaterialOtherDescription")?.trim()) {
            toast.error("Please provide a description when selecting 'Other' for Tank 1 Material Type");
            return;
        }
        
        if (watch("tankMaterialId2") === -1 && !watch("tank2MaterialOtherDescription")?.trim()) {
            toast.error("Please provide a description when selecting 'Other' for Tank 2 Material Type");
            return;
        }

        if (formRef.current) {
            formRef.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
            );
        }
    }


    let showTankSizeComment = watch("tankComplianceId");
    let showTankMaterialComment = watch("tankMaterialComplianceId");
    let showOutletDeviceComment = watch("outletDeviceComplianceId");
    let showMultiChamberedComment = watch("multiChamberedComplianceId");
    // In Series uses MultiChambered compliance for required checks
    let showInSeriesComment = showMultiChamberedComment;
    let showOutletFilterComment = watch("outletFilterComplienceId");
    let showLegendComment = watch("legendComplianceId");
    let showWatertightComment = watch("watertightComplianceId");
    let showLevelComment = watch("levelComplianceId");
    let showDepthToLidComment = watch("depthToLidComplianceId");
    let showTank2SizeComment = watch("tank2ComplianceId");
    let showTank2MaterialComment = watch("tank2MaterialComplianceId");
    let showTank2LegendComment = watch("tank2LegendComplianceId");
    let showTank2OutletDeviceComment = watch("tank2OutletDeviceComplianceId");
    let showTank2MultiChamberedComment = watch("tank2MultiChamberedComplianceId");
    // Tank 2 In Series uses Tank2 MultiChambered compliance for required checks
    let showTank2InSeriesComment = showTank2MultiChamberedComment;
    let showTank2OutletFilterComment = watch("tank2OutletFilterComplianceId");
    let showTank2WatertightComment = watch("tank2WatertightComplianceId");
    let showTank2LevelComment = watch("tank2LevelComplianceId");
    let showTank2DepthToLidComment = watch("tank2DepthToLidComplianceId");
    let showAtuPbtComment = watch("atuPbtComplianceId");
    let showTank2AtuPbtComment = watch("tank2AtuPbtComplianceId");
    let markAllCompliance = watch("markAll");
    let showTank = watch("isTank2Showable");
    //setTank2(showTank);
    const h1Styles: CSS.Properties = {
        float: "right",
        marginTop: '27px'
    };
    const markClick = (e: any) => {
        setValue('tankComplianceId', markAllCompliance);
        setValue('tankMaterialComplianceId', markAllCompliance);
        setValue('outletDeviceComplianceId', markAllCompliance);
        setValue('multiChamberedComplianceId', markAllCompliance);
        setValue('outletFilterComplienceId', markAllCompliance);
        setValue('legendComplianceId', markAllCompliance);
        setValue('watertightComplianceId', markAllCompliance);
        setValue('levelComplianceId', markAllCompliance);
        setValue('depthToLidComplianceId', markAllCompliance);
        setValue('atuPbtComplianceId', markAllCompliance);
        if (showTank == true) {
            setValue('tank2ComplianceId', markAllCompliance);
            setValue('tank2MaterialComplianceId', markAllCompliance);
            setValue('tank2LegendComplianceId', markAllCompliance);
            setValue('tank2OutletDeviceComplianceId', markAllCompliance);
            setValue('tank2MultiChamberedComplianceId', markAllCompliance);
            setValue('tank2OutletFilterComplianceId', markAllCompliance);
            setValue('tank2WatertightComplianceId', markAllCompliance);
            setValue('tank2LevelComplianceId', markAllCompliance);
            setValue('tank2DepthToLidComplianceId', markAllCompliance);
            setValue('tank2AtuPbtComplianceId', markAllCompliance);
        }

        e.preventDefault();
    };

    return (
        <form id={props.id} ref={formRef} onSubmit={handleSubmit((data: TankInstallationInput) => {
            const sanitized = {
                ...data,
                tankMaterialId1: (Number(data.tankMaterialId1) === -1 ? null : data.tankMaterialId1) as any,
                tankMaterialId2: (Number(data.tankMaterialId2) === -1 ? null : data.tankMaterialId2) as any,
            } as any;
            onSubmit(sanitized);
        })}>
            <div className="row">
                <div className="col col-md-8">
                    <div className="card-main-title">
                        Tank Installation
                    </div>
                </div>
                <div className="col col-md-3">
                    <FormControl.Select
                        control={control}
                        name="markAll"
                        label="Mark All"
                        options={complianceData}
                        required
                    />
                </div>
                <div className="col col-md-1" style={h1Styles}>
                    <Button name="markAll" className="btn btn-primary" onClick={markClick} >Apply</Button>
                </div>
            </div>

            <Card.Container>

                <Card.Header>
                    <Card.Title>{props.title}</Card.Title>
                </Card.Header>

                <Card.Body>
                    {/*  ============================================================================================================================================*/}
                    <Layout.Row>   <>
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankComplianceId"
                                    label="Tank Size"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Text
                                    control={control}
                                    name="tankSize1"
                                    label="Tank Size"
                                    required={showTankSizeComment != Constants.Not_Applicable && showTankSizeComment != Constants.Unobserved}
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankSize1Units"
                                    label="Units (gal/gpd)"
                                    options={[
                                        { id: '', name: 'Please select a value' },
                                        { id: 'gal', name: 'Gallons' },
                                        { id: 'gpd', name: 'Gallons Per Day' }
                                    ]}
                                    required={showTankSizeComment != Constants.Not_Applicable && showTankSizeComment != Constants.Unobserved}
                                />
                            </div>
                        </div>



                        {showTankSizeComment &&
                            showTankSizeComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="tankSizeComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        {/*                                                     ATU/PBTS                                                                                   */}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="atuPbtComplianceId"
                                    label="ATU/PBTS"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="atuPbtId"
                                    label="Make and Model"
                                    options={atuPbtData}
                                    required={showAtuPbtComment != Constants.Not_Applicable && showAtuPbtComment != Constants.Unobserved}
                                />
                            </div>
                        </div>
                        {showAtuPbtComment &&
                            showAtuPbtComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="atuPbtComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankMaterialComplianceId"
                                    label="Tank Material"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="tankMaterialId1"
                                    label="Tank Material Type"
                                    options={[
                                        ...tankMaterialData,
                                        { id: -1, name: 'Other' }
                                    ]}
                                    required={showTankMaterialComment != Constants.Not_Applicable && showTankMaterialComment != Constants.Unobserved && Number(watch("tankMaterialId1")) !== -1}
                                />
                            </div>
                            {/* Tank Material Other Description field - inline with material type */}
                            {Number(watch("tankMaterialId1")) === -1 && (
                                <div className="col col-md-4">
                                    <FormControl.Text
                                        control={control}
                                        name="tankMaterialOtherDescription"
                                        label="Other (Description)"
                                        placeholder="Please describe the material type"
                                        required={showTankMaterialComment != Constants.Not_Applicable && showTankMaterialComment != Constants.Unobserved}
                                    />
                                </div>
                            )}
                        </div>
                        {showTankMaterialComment &&
                            showTankMaterialComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="tankMaterialComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="outletDeviceComplianceId"
                                    label="Inlet / Outlet Device"
                                    options={complianceData}
                                    required
                                />
                            </div>

                        </div>
                        {showOutletDeviceComment &&
                            showOutletDeviceComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="outletDeviceComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="multiChamberedComplianceId"
                                    label="Tank Multichambered"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="multiChambered"
                                    label="Is Tank Multichambered?"
                                    options={[
                                        { id: '', name: 'Please select a value' },
                                        { id: 1, name: 'Yes' },
                                        { id: 0, name: 'No' }
                                    ]}
                                    required={showMultiChamberedComment != Constants.Not_Applicable && showMultiChamberedComment != Constants.Unobserved}
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="inSeries"
                                    label="Tanks In Series?"
                                    options={[
                                        { id: '', name: 'Please select a value' },
                                        { id: 1, name: 'Yes' },
                                        { id: 0, name: 'No' }
                                    ]}
                                    required={showMultiChamberedComment != Constants.Not_Applicable && showMultiChamberedComment != Constants.Unobserved}
                                />
                            </div>
                        </div>
                        {showMultiChamberedComment &&
                            showMultiChamberedComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="multiChamberedComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="outletFilterComplienceId"
                                    label="Tank Outlet Filter"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="outletFilterId"
                                    label="Outlet Filter Make & Model"
                                    options={outletFilterData}
                                    required={showOutletFilterComment != Constants.Not_Applicable && showOutletFilterComment != Constants.Out_of_Compliance && showOutletFilterComment != Constants.Unobserved}
                                />
                            </div>
                        </div>
                        {showOutletFilterComment &&
                            showOutletFilterComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="outletFilterComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="legendComplianceId"
                                    label="Tank Legend"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="legendId1"
                                    label="Tank Legend Number"
                                    options={legendData}
                                    required
                                />
                            </div>
                        </div>
                        {showLegendComment &&
                            showLegendComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="legendComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="watertightComplianceId"
                                    label="Watertight"
                                    options={complianceData}
                                    required
                                />
                            </div>

                        </div>
                        {showWatertightComment &&
                            showWatertightComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="watertightComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="levelComplianceId"
                                    label="Level"
                                    options={complianceData}
                                    required
                                />
                            </div>

                        </div>
                        {showLevelComment &&
                            showLevelComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="levelComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                        {/*  ============================================================================================================================================*/}
                        <div className="row">
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="depthToLidComplianceId"
                                    label="Depth to Lid"
                                    options={complianceData}
                                    required
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Text
                                    control={control}
                                    name="depthToLidInches"
                                    label="Depth to Lid (inches)"
                                    required={showDepthToLidComment != Constants.Not_Applicable && showDepthToLidComment != Constants.Unobserved}
                                />
                            </div>
                            <div className="col col-md-4">
                                <FormControl.Select
                                    control={control}
                                    name="risers"
                                    label="Riser(s)"
                                    options={[
                                        { id: '', name: 'Please select a value' },
                                        { id: 1, name: 'Yes' },
                                        { id: 0, name: 'No' }
                                    ]}
                                    required={showDepthToLidComment != Constants.Not_Applicable && showDepthToLidComment != Constants.Unobserved}
                                />
                            </div>
                        </div>
                        {showDepthToLidComment &&
                            showDepthToLidComment == 3 && (
                            <div className="row">
                                <div className="col col-md-12">
                                    <FormControl.TextArea
                                        control={control}
                                        name="depthToLidComment"
                                        label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/*  ============================================================================================================================================*/}
                        <div className="row">

                            <div className="col col-md-4" hidden>
                                <FormControl.Text
                                    control={control}
                                    name="InspectionStatusId"
                                    label="Inspection StatusId"
                                    required
                                />
                            </div>

                        </div>
                        {/*  ============================================================================================================================================*/}
                    </></Layout.Row>
                </Card.Body>



                <div hidden={isTank2Showable}>
                    <Button id="showme" className="btn btn-primary ms-auto px-3 py-2" onClick={(e) => {
                        e.preventDefault();
                        setIsTank2Showable(true);
                        setValue('isTank2Showable', true);
                    }}>Add Tank</Button>
                </div>
                {
                    isTank2Showable && (<Card.Body>
                        {/*  ============================================================================================================================================*/}
                        {/*                                                     Tank 2                                                                                   */}
                        {/*  ============================================================================================================================================*/}

                        <div className="row mb-4">
                            <div className="col col-md-8">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                        <span className="fw-bold">2</span>
                                    </div>
                                    <div>
                                        <h3 className="text-primary mb-1 fw-bold">Tank 2</h3>
                                        <p className="text-muted mb-0">Additional tank installation details</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col col-md-4 text-end">
                                <Button className="btn btn-danger" onClick={(e) => {
                                    e.preventDefault();
                                    setIsTank2Showable(false);
                                    setValue('isTank2Showable', false);
                                }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash me-2" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M4 7l16 0"></path>
                                        <path d="M10 11l0 6"></path>
                                        <path d="M14 11l0 6"></path>
                                        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                    </svg>
                                    Remove Tank
                                </Button>
                            </div>
                        </div>

                        <div className="border-top border-2 border-primary pt-4">
                            {/* Tank 2 Size */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2ComplianceId"
                                        label="Tank Size"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Text
                                        control={control}
                                        name="tankSize2"
                                        label="Tank Size"
                                        required={showTank2SizeComment != Constants.Not_Applicable
                                            && showTank2SizeComment != Constants.Unobserved}
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tankSize2Units"
                                        label="Units (gal/gpd)"
                                        options={[
                                            { id: '', name: 'Please select a value' },
                                            { id: 'gal', name: 'Gallons' },
                                            { id: 'gpd', name: 'Gallons Per Day' }
                                        ]}
                                        required={showTank2SizeComment != Constants.Not_Applicable
                                            && showTank2SizeComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2SizeComment &&
                                showTank2SizeComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2SizeComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                            {/* Tank 2 ATU/PBTS */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2AtuPbtComplianceId"
                                        label="ATU/PBTS"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2AtuPbtId"
                                        label="Make and Model"
                                        options={atuPbtData}
                                        required={showTank2AtuPbtComment != Constants.Not_Applicable
                                            && showTank2AtuPbtComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2AtuPbtComment &&
                                showTank2AtuPbtComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2AtuPbtComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Material */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2MaterialComplianceId"
                                        label="Tank Material"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tankMaterialId2"
                                        label="Tank Material Type"
                                        options={[
                                            ...tankMaterialData,
                                            { id: -1, name: 'Other' }
                                        ]}
                                        required={showTank2MaterialComment != Constants.Not_Applicable
                                            && showTank2MaterialComment != Constants.Unobserved
                                            && Number(watch("tankMaterialId2")) !== -1}
                                    />
                                </div>
                                {/* Tank Material Other Description field - inline with material type */}
                                {Number(watch("tankMaterialId2")) === -1 && (
                                    <div className="col col-md-4">
                                        <FormControl.Text
                                            control={control}
                                            name="tank2MaterialOtherDescription"
                                            label="Other (Description)"
                                            placeholder="Please describe the material type"
                                            required={showTank2MaterialComment != Constants.Not_Applicable
                                                && showTank2MaterialComment != Constants.Unobserved}
                                        />
                                    </div>
                                )}
                            </div>
                            {showTank2MaterialComment &&
                                showTank2MaterialComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2MaterialComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Outlet Device */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2OutletDeviceComplianceId"
                                        label="Inlet / Outlet Device"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                            </div>
                            {showTank2OutletDeviceComment &&
                                showTank2OutletDeviceComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2OutletDeviceComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Multichambered + In Series (inline to match Tank 1) */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2MultiChamberedComplianceId"
                                        label="Tank Multichambered"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2MultiChambered"
                                        label="Is Tank Multichambered?"
                                        options={[
                                            { id: '', name: 'Please select a value' },
                                            { id: 1, name: 'Yes' },
                                            { id: 0, name: 'No' }
                                        ]}
                                        required={showTank2MultiChamberedComment != Constants.Not_Applicable
                                            && showTank2MultiChamberedComment != Constants.Unobserved}
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2InSeries"
                                        label="Tanks In Series?"
                                        options={[
                                            { id: '', name: 'Please select a value' },
                                            { id: 1, name: 'Yes' },
                                            { id: 0, name: 'No' }
                                        ]}
                                        required={showTank2MultiChamberedComment != Constants.Not_Applicable
                                            && showTank2MultiChamberedComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2MultiChamberedComment &&
                                showTank2MultiChamberedComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2MultiChamberedComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            

                            {/* Tank 2 Outlet Filter */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2OutletFilterComplianceId"
                                        label="Tank Outlet Filter"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2OutletFilterId"
                                        label="Outlet Filter Make and Model"
                                        options={outletFilterData}
                                        required={showTank2OutletFilterComment != Constants.Not_Applicable
                                            && showTank2OutletFilterComment != Constants.Out_of_Compliance
                                            && showTank2OutletFilterComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2OutletFilterComment &&
                                showTank2OutletFilterComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2OutletFilterComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Legend */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2LegendComplianceId"
                                        label="Tank Legend"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="legendId2"
                                        label="Tank Legend Number"
                                        options={legendData}
                                        required={showTank2LegendComment != Constants.Not_Applicable
                                            && showTank2LegendComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2LegendComment &&
                                showTank2LegendComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2LegendComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Watertight */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2WatertightComplianceId"
                                        label="Watertight"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                            </div>
                            {showTank2WatertightComment &&
                                showTank2WatertightComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2WatertightComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Level */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2LevelComplianceId"
                                        label="Level"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                            </div>
                            {showTank2LevelComment &&
                                showTank2LevelComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2LevelComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Tank 2 Depth to Lid */}
                            <div className="row">
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2DepthToLidComplianceId"
                                        label="Depth to Lid"
                                        options={complianceData}
                                        required
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Text
                                        control={control}
                                        name="tank2DepthToLidInches"
                                        label="Depth to Lid (inches)"
                                        required={showTank2DepthToLidComment != Constants.Not_Applicable && showTank2DepthToLidComment != Constants.Unobserved}
                                    />
                                </div>
                                <div className="col col-md-4">
                                    <FormControl.Select
                                        control={control}
                                        name="tank2Risers"
                                        label="Riser(s)"
                                        options={[
                                            { id: '', name: 'Please select a value' },
                                            { id: 1, name: 'Yes' },
                                            { id: 0, name: 'No' }
                                        ]}
                                        required={showTank2DepthToLidComment != Constants.Not_Applicable && showTank2DepthToLidComment != Constants.Unobserved}
                                    />
                                </div>
                            </div>
                            {showTank2DepthToLidComment &&
                                showTank2DepthToLidComment == 3 && (
                                <div className="row">
                                    <div className="col col-md-12">
                                        <FormControl.TextArea
                                            control={control}
                                            name="tank2DepthToLidComment"
                                            label="Explanation of Compliance Violation (upload any related sketches or documentation on the final submission page)"
                                            required
                                        />
                                    </div>
                                </div>
                            )}


                        </div>
                    </Card.Body>)
                }


                {/*=======================================================================================================================================================*/}
                <Card.Footer>
                    <div className="d-flex">
                        <span className="btn btn-link" onClick={(e) => {
                            e.preventDefault();
                            setValue('inspectionStatusId', InspectionStatus.Incomplete);
                            submit()
                        }}><svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-circle-arrow-left" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18"></path>
                                <path d="M8 12l4 4"></path><path d="M8 12h8"></path>
                                <path d="M12 8l-4 4"></path>
                            </svg> Save and Exit
                        </span>
                        <div className="btn-list ms-auto">
                            <Link
                                to={{
                                    pathname: props.returnPath ?? path.current,
                                    state: { applicationNumber: applicationNumber },
                                }}
                                className="btn btn-light"
                                role="button">
                                Cancel
                            </Link>
                            <Button className="btn btn-primary ms-auto" onClick={(e) => {
                                e.preventDefault();
                                setValue('inspectionStatusId', InspectionStatus.Complete);
                                submit()
                            }}>Next</Button>
                        </div>
                    </div>
                </Card.Footer>
            </Card.Container>


        </form>
    );
};

export default TankInstallationForm;




